import BudgetCheckLeadWall from "@modules/application/components/BudgetCheckLeadWall";
import BudgetCheckLoading from "@modules/application/components/BudgetCheckLoading";
import BudgetCheckResult from "@modules/application/components/BudgetCheckResult";
import EmbeddedBudgetCheckLeadWall from "@modules/application/components/EmbeddedBudgetCheckLeadWall";
import EmbeddedBudgetFinalScreen from "@modules/application/components/EmbeddedBudgetFinalScreen";
import { FormStep } from "@modules/application/components/FormSteps/FormSteps";
import { TFunction } from "next-i18next";
import {
  getEmployedIncomeStep,
  getEmploymentTypeStep,
  getEquityStep,
  getFederalStateStep,
  getPropertyTypeStep,
  getRetiredIncomeStep,
  getSelfEmployedIncomeStep,
} from "./steps";
import { BudgetCheckFormValues, BudgetCheckStepId } from "./types";

const getBudgetCheckSteps = (
  t: TFunction,
  locale: string,
  isEmbedded = false,
  hasLead = false,
  partnerSlug,
  withSearchProfile = false,
): FormStep<BudgetCheckFormValues>[] => {
  const steps: FormStep<BudgetCheckFormValues>[] = [];

  steps.push({
    id: BudgetCheckStepId.federalState,
    label: t("federal_state_sidebar_title"),
    config: getFederalStateStep(t, withSearchProfile),
    transition: withSearchProfile
      ? BudgetCheckStepId.propertyType
      : BudgetCheckStepId.employmentType,
  });

  // only shown if withSearchProfile is true
  steps.push({
    id: BudgetCheckStepId.propertyType,
    label: t("property_type_sidebar_title"),
    config: getPropertyTypeStep(t),
    transition: BudgetCheckStepId.employmentType,
  });

  steps.push({
    id: BudgetCheckStepId.employmentType,
    label: t("employment_type_title"),
    config: getEmploymentTypeStep(t),
    possibleTransitions: [
      BudgetCheckStepId.selfEmployedIncome,
      BudgetCheckStepId.retiredIncome,
      BudgetCheckStepId.equity,
      BudgetCheckStepId.employedIncome,
    ],
    transition: (v) => {
      if (
        v.employmentType === "FREELANCER" ||
        v.employmentType === "SELF_EMPLOYED"
      ) {
        return BudgetCheckStepId.selfEmployedIncome;
      }
      if (v.employmentType === "RETIRED") {
        return BudgetCheckStepId.retiredIncome;
      }
      if (v.employmentType === "UNEMPLOYED") {
        return BudgetCheckStepId.equity;
      }
      return BudgetCheckStepId.employedIncome;
    },
  });

  steps.push({
    id: BudgetCheckStepId.employedIncome,
    label: t("income_title"),
    config: getEmployedIncomeStep(t),
    transition: BudgetCheckStepId.equity,
  });

  steps.push({
    id: BudgetCheckStepId.retiredIncome,
    label: t("income_title"),
    config: getRetiredIncomeStep(t),
    transition: BudgetCheckStepId.equity,
  });

  steps.push({
    id: BudgetCheckStepId.selfEmployedIncome,
    label: t("income_title"),
    config: getSelfEmployedIncomeStep(t),
    transition: BudgetCheckStepId.equity,
  });

  steps.push({
    id: BudgetCheckStepId.equity,
    label: t("equity_title"),
    config: getEquityStep(t),
    transition: hasLead ? BudgetCheckStepId.loading : BudgetCheckStepId.contact,
  });

  steps.push({
    id: BudgetCheckStepId.loading,
    config: {},
    component: BudgetCheckLoading,
    transition: BudgetCheckStepId.result,
    hidden: true,
  });

  if (isEmbedded) {
    steps.push({
      id: BudgetCheckStepId.contact,
      config: {},
      component: EmbeddedBudgetCheckLeadWall,
      transition: BudgetCheckStepId.result,
      hidden: true,
    });
    steps.push({
      id: BudgetCheckStepId.result,
      config: {},
      component: EmbeddedBudgetFinalScreen,
      isResult: true,
      hidden: true,
      label: t("maximum_budget_result_title"),
    });
  } else {
    steps.push({
      id: BudgetCheckStepId.contact,
      config: {},
      component: BudgetCheckLeadWall,
      transition: BudgetCheckStepId.result,
      hidden: true,
    });
    steps.push({
      id: BudgetCheckStepId.result,
      component: BudgetCheckResult,
      config: {},
      isResult: true,
      label: t("maximum_budget_result_title"),
    });
  }

  return steps;
};

export default getBudgetCheckSteps;
